import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from '../librarianSwal';
import { useActiveLibraryId, useSectionBase } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  getBookById,
  getActiveBookCategoriesByLibrary,
  updateBook,
  getErrorMessage
} from '../../api/api';
import '../BookList.css';
import '../lib-page-header.css';

const EditBook = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const sectionBase = useSectionBase();

  const libraryId = useActiveLibraryId();

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    library_id: libraryId || '',
    category_id: '',
    title: '',
    subtitle: '',
    author_name: '',
    publisher_name: '',
    isbn: '',
    edition: '',
    language: '',
    publish_year: '',
    description: '',
    cover_image: '',
    late_fine_per_day_override: '',
    lost_charge_override: '',
    damage_charge_override: '',
    status: 'active'
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        if (!libraryId) return;

        const data = await getActiveBookCategoriesByLibrary(libraryId);
        setCategories(data || []);
      } catch (error) {
        Swal.fire('Error', getErrorMessage(error), 'error');
      }
    };

    fetchCategories();
  }, [libraryId]);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        if (!id) return;

        setLoading(true);

        const data = await getBookById(id);

        setFormData({
          library_id: data.library_id || libraryId || '',
          category_id: data.category_id || '',
          title: data.title || '',
          subtitle: data.subtitle || '',
          author_name: data.author_name || '',
          publisher_name: data.publisher_name || '',
          isbn: data.isbn || '',
          edition: data.edition || '',
          language: data.language || '',
          publish_year: data.publish_year || '',
          description: data.description || '',
          cover_image: data.cover_image || '',
          late_fine_per_day_override: data.late_fine_per_day_override || '',
          lost_charge_override: data.lost_charge_override || '',
          damage_charge_override: data.damage_charge_override || '',
          status: data.status || 'active'
        });
      } catch (error) {
        console.error('Fetch book error:', error);
        Swal.fire('Error', getErrorMessage(error), 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
  }, [id, libraryId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.library_id) {
      Swal.fire('Error', 'Library ID not found', 'error');
      return false;
    }

    if (!formData.category_id) {
      Swal.fire('Error', 'Please select book category', 'error');
      return false;
    }

    if (!formData.title.trim()) {
      Swal.fire('Error', 'Book title is required', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        library_id: formData.library_id,
        category_id: formData.category_id,
        title: formData.title.trim(),
        subtitle: formData.subtitle || null,
        author_name: formData.author_name || null,
        publisher_name: formData.publisher_name || null,
        isbn: formData.isbn || null,
        edition: formData.edition || null,
        language: formData.language || null,
        publish_year: formData.publish_year || null,
        description: formData.description || null,
        cover_image: formData.cover_image || null,
        late_fine_per_day_override: formData.late_fine_per_day_override || null,
        lost_charge_override: formData.lost_charge_override || null,
        damage_charge_override: formData.damage_charge_override || null,
        status: formData.status || 'active'
      };

      await updateBook(id, payload);

      await Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Book updated successfully'
      });

      navigate(`${sectionBase}/books`);
    } catch (error) {
      console.error('Update book error:', error);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: getErrorMessage(error)
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bd-page">

      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-book" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Edit Book</h3>
            <div className="lib-page-header__subtitle">Update book master details</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button className="bd-btn bd-btn-back" onClick={() => navigate(`${sectionBase}/books`)}>
            <i className="ti ti-arrow-left" /> Back
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bd-card-none">
          <div className="bd-card-head">
            <i className="ti ti-book-2" /> Book Information
          </div>

          <div className="bd-card-body">
            {loading ? (
              <Loader />
            ) : (
              <div className="bd-form-grid">
                <div className="bd-form-group">
                  <label>Category <span className="req">*</span></label>
                  <SearchableSelect
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    options={categories.map((category) => ({ value: category.id, label: category.name }))}
                    placeholder="Select Category"
                    required
                    clearable={false}
                  />
                </div>

                <div className="bd-form-group">
                  <label>Book Title <span className="req">*</span></label>
                  <input
                    type="text"
                    name="title"
                    className="bd-input"
                    placeholder="Enter book title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="bd-form-group">
                  <label>Subtitle</label>
                  <input
                    type="text"
                    name="subtitle"
                    className="bd-input"
                    placeholder="Enter subtitle"
                    value={formData.subtitle}
                    onChange={handleChange}
                  />
                </div>

                <div className="bd-form-group">
                  <label>Author</label>
                  <input
                    type="text"
                    name="author_name"
                    className="bd-input"
                    placeholder="Enter author name"
                    value={formData.author_name}
                    onChange={handleChange}
                  />
                </div>

                <div className="bd-form-group">
                  <label>Publisher</label>
                  <input
                    type="text"
                    name="publisher_name"
                    className="bd-input"
                    placeholder="Enter publisher name"
                    value={formData.publisher_name}
                    onChange={handleChange}
                  />
                </div>

                <div className="bd-form-group">
                  <label>ISBN</label>
                  <input
                    type="text"
                    name="isbn"
                    className="bd-input"
                    placeholder="Enter ISBN"
                    value={formData.isbn}
                    onChange={handleChange}
                  />
                </div>

                <div className="bd-form-group">
                  <label>Edition</label>
                  <input
                    type="text"
                    name="edition"
                    className="bd-input"
                    value={formData.edition}
                    onChange={handleChange}
                  />
                </div>

                <div className="bd-form-group">
                  <label>Language</label>
                  <input
                    type="text"
                    name="language"
                    className="bd-input"
                    value={formData.language}
                    onChange={handleChange}
                  />
                </div>

                <div className="bd-form-group">
                  <label>Publish Year</label>
                  <input
                    type="number"
                    name="publish_year"
                    className="bd-input"
                    value={formData.publish_year}
                    onChange={handleChange}
                  />
                </div>

                <div className="bd-form-group">
                  <label>Status</label>
                  <SearchableSelect
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' }
                    ]}
                    clearable={false}
                  />
                </div>

                <div className="bd-form-group">
                  <label>Late Fine Override</label>
                  <input
                    type="number"
                    name="late_fine_per_day_override"
                    className="bd-input"
                    value={formData.late_fine_per_day_override}
                    onChange={handleChange}
                  />
                </div>

                <div className="bd-form-group">
                  <label>Lost Charge Override</label>
                  <input
                    type="number"
                    name="lost_charge_override"
                    className="bd-input"
                    value={formData.lost_charge_override}
                    onChange={handleChange}
                  />
                </div>

                <div className="bd-form-group">
                  <label>Damage Charge Override</label>
                  <input
                    type="number"
                    name="damage_charge_override"
                    className="bd-input"
                    value={formData.damage_charge_override}
                    onChange={handleChange}
                  />
                </div>

                <div className="bd-form-group bd-col-full">
                  <label>Description</label>
                  <textarea
                    name="description"
                    rows="4"
                    className="bd-input"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="bd-form-actions">
            <button
              type="button"
              className="bd-btn bd-btn-back"
              onClick={() => navigate(`${sectionBase}/books`)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bd-btn bd-btn-primary"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Book'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditBook;